<script setup>
// Notas internas · "leído" entre agentes (Victor 2026-05-27).
// V2 · 2026-05-30 (Victor): pill compacto + dropdown al click (estilo P1).
// Antes: cuadro grande a la derecha que listaba a todos (incluso quien no vio).
// Ahora: a la IZQUIERDA debajo de la nota, solo aparece si alguien la vio.
// Click despliega lista con nombre + hora.
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  // Mensaje (nota privada) en camelCase.
  message: { type: Object, required: true },
  // Agentes que han participado en la conversación: [{ id, name }].
  participants: { type: Array, default: () => [] },
  currentUserId: { type: [Number, String], default: null },
});

const open = ref(false);

const receipts = computed(() => {
  const r = props.message?.contentAttributes?.noteReadReceipts;
  return Array.isArray(r) ? r : [];
});
const authorId = computed(
  () => props.message?.senderId ?? props.message?.sender?.id ?? null
);
const readerIds = computed(() => new Set(receipts.value.map(r => r.userId)));
const pending = computed(() =>
  props.participants.filter(
    p => p.id !== authorId.value && !readerIds.value.has(p.id)
  )
);
// V2: solo aparece si alguien YA vio. Si nadie vio, no satura la nota.
const hasReaders = computed(() => receipts.value.length > 0);

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m} ${ampm}`;
}
function initials(name) {
  if (!name) return '?';
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(s => s[0]?.toUpperCase() || '')
      .join('') || '?'
  );
}
function toggle() { open.value = !open.value; }
function close() { open.value = false; }
</script>

<template>
  <div v-if="hasReaders" class="flex justify-start px-4 -mt-1 mb-2.5">
    <div class="relative" v-on-click-outside="close">
      <button
        type="button"
        class="nr-pill"
        :class="{ 'nr-pill-open': open }"
        @click="toggle"
      >
        <span class="nr-eye">👁</span>
        <span>Visto por <b>{{ receipts.length }}</b></span>
        <span class="nr-arr">▾</span>
      </button>
      <div v-if="open" class="nr-drop">
        <div
          v-for="r in receipts"
          :key="`r-${r.userId}`"
          class="nr-row"
        >
          <span class="nr-av">{{ initials(r.name) }}</span>
          <div class="nr-info">
            <div class="nr-nm">{{ r.name }}</div>
            <div class="nr-tm">✓ {{ fmtTime(r.readAt) }}</div>
          </div>
        </div>
        <div
          v-for="p in pending"
          :key="`p-${p.id}`"
          class="nr-row nr-row-pending"
        >
          <span class="nr-av nr-av-mute">{{ initials(p.name) }}</span>
          <div class="nr-info">
            <div class="nr-nm">{{ p.name }}</div>
            <div class="nr-tm nr-tm-pending">sin ver</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nr-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 10px;
  color: rgba(255,255,255,0.62);
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s ease;
  line-height: 1.4;
}
.nr-pill:hover,
.nr-pill-open {
  background: rgba(201,163,90,0.18);
  border-color: rgba(201,163,90,0.40);
  color: #c9a35a;
}
.nr-pill b {
  font-weight: 800;
  color: #fff;
  margin: 0 2px;
}
.nr-pill-open b { color: #c9a35a; }
.nr-eye { font-size: 10px; opacity: 0.9; }
.nr-arr {
  font-size: 8.5px;
  color: rgba(255,255,255,0.40);
  transition: transform 0.18s ease;
}
.nr-pill-open .nr-arr {
  transform: rotate(180deg);
  color: #c9a35a;
}
.nr-drop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  max-width: 300px;
  background: linear-gradient(180deg, #1b1b21, #15151a);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 11px;
  overflow: hidden;
  box-shadow:
    0 20px 40px rgba(0,0,0,0.6),
    0 0 0 1px rgba(201,163,90,0.10) inset;
  padding: 5px 0;
  z-index: 30;
}
.nr-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 13px;
}
.nr-av {
  width: 22px; height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #3a2d3a, #2a1d2a);
  border: 1px solid rgba(255,255,255,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 800;
  color: #fff;
}
.nr-av-mute {
  background: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.50);
  border-color: rgba(255,255,255,0.08);
}
.nr-info { flex: 1; min-width: 0; }
.nr-nm {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nr-tm {
  font-size: 9.5px;
  color: #86efac;
  margin-top: 1px;
  font-weight: 600;
}
.nr-row-pending .nr-nm { color: rgba(255,255,255,0.55); }
.nr-tm-pending { color: rgba(252,165,165,0.85); font-weight: 600; }
</style>
